import { z } from "zod";

export interface AiTokenUsage {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  totalTokens: number;
  requestCount: number;
}

export const aiTokenUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedInputTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  requestCount: z.number().int().nonnegative(),
});

export function emptyAiTokenUsage(): AiTokenUsage {
  return {
    inputTokens: 0,
    outputTokens: 0,
    cachedInputTokens: 0,
    totalTokens: 0,
    requestCount: 0,
  };
}

function readNonNegativeInt(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.floor(value);
}

function readUsageRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") return null;
  return raw as Record<string, unknown>;
}

/** 将各 provider 的 usage 字段归一化为统一结构（单次请求） */
export function normalizeAiTokenUsage(raw: unknown): AiTokenUsage {
  const usage = readUsageRecord(raw);
  if (!usage) {
    return emptyAiTokenUsage();
  }

  const details = readUsageRecord(usage.prompt_tokens_details);
  const inputTokenDetails = readUsageRecord(usage.inputTokenDetails);
  const deepseekCacheHit = readNonNegativeInt(usage.prompt_cache_hit_tokens);
  const deepseekCacheMiss = readNonNegativeInt(usage.prompt_cache_miss_tokens);
  const noCacheInputTokens = readNonNegativeInt(inputTokenDetails?.noCacheTokens);
  const cacheReadInputTokens = readNonNegativeInt(inputTokenDetails?.cacheReadTokens);

  let inputTokens = readNonNegativeInt(usage.inputTokens);
  if (!inputTokens) inputTokens = readNonNegativeInt(usage.prompt_tokens);
  if (!inputTokens && (noCacheInputTokens || cacheReadInputTokens)) {
    inputTokens = noCacheInputTokens + cacheReadInputTokens;
  }
  if (!inputTokens && (deepseekCacheHit || deepseekCacheMiss)) {
    inputTokens = deepseekCacheHit + deepseekCacheMiss;
  }

  let outputTokens = readNonNegativeInt(usage.outputTokens);
  if (!outputTokens) outputTokens = readNonNegativeInt(usage.completion_tokens);

  let cachedInputTokens = readNonNegativeInt(usage.cachedInputTokens);
  if (!cachedInputTokens) cachedInputTokens = cacheReadInputTokens;
  if (!cachedInputTokens) cachedInputTokens = readNonNegativeInt(details?.cached_tokens);
  if (!cachedInputTokens) cachedInputTokens = deepseekCacheHit;

  let totalTokens = readNonNegativeInt(usage.totalTokens);
  if (!totalTokens) totalTokens = readNonNegativeInt(usage.total_tokens);
  if (!totalTokens) totalTokens = inputTokens + outputTokens;

  return {
    inputTokens,
    outputTokens,
    cachedInputTokens,
    totalTokens,
    requestCount: 1,
  };
}

export function addAiTokenUsage(left: AiTokenUsage, right: AiTokenUsage): AiTokenUsage {
  return {
    inputTokens: left.inputTokens + right.inputTokens,
    outputTokens: left.outputTokens + right.outputTokens,
    cachedInputTokens: left.cachedInputTokens + right.cachedInputTokens,
    totalTokens: left.totalTokens + right.totalTokens,
    requestCount: left.requestCount + right.requestCount,
  };
}

export function sumAiTokenUsage(usages: AiTokenUsage[]): AiTokenUsage {
  return usages.reduce((acc, item) => addAiTokenUsage(acc, item), emptyAiTokenUsage());
}

export function formatTokenCount(value: number): string {
  return value.toLocaleString("zh-CN");
}
